// frontend/src/pages/KanbanTableros.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import kanbanService from '../services/kanbanService';
import KanbanTableroModal from '../components/KanbanTableroModal';
import { toast } from 'react-toastify';
import Loading from '../components/Loading';
import './KanbanTableros.css';

const KanbanTableros = () => {
  const [activeTab, setActiveTab] = useState('mis-tableros');
  const [misTableros, setMisTableros] = useState([]);
  const [tablerosCompartidos, setTablerosCompartidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const [mostrarArchivados, setMostrarArchivados] = useState(false);
  const [searchMis, setSearchMis] = useState('');
  const [searchCompartidos, setSearchCompartidos] = useState('');
  const [selectedBoardId, setSelectedBoardId] = useState(null);

  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    icono: '📋',
    color: '#3b82f6',
    is_private: false
  });

  useEffect(() => {
    cargarTableros();
  }, []);

  const cargarTableros = async () => {
    try {
      setLoading(true);
      const [misData, compartidosData] = await Promise.all([
        kanbanService.getMyBoards(),
        kanbanService.getSharedBoards()
      ]);
      setMisTableros(misData.boards || []);
      setTablerosCompartidos(compartidosData.boards || []);
    } catch (error) {
      console.error('Error al cargar tableros:', error);
      toast.error('Error al cargar tableros');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBoard = async (e) => {
    e.preventDefault();
    try {
      await kanbanService.createBoard(formData);
      toast.success('Tablero creado exitosamente');
      setShowCreateModal(false);
      setFormData({
        nombre: '',
        descripcion: '',
        icono: '📋',
        color: '#3b82f6',
        is_private: false
      });
      cargarTableros();
    } catch (error) {
      console.error('Error al crear tablero:', error);
      toast.error('Error al crear tablero');
    }
  };

  const handleEditBoard = async (e) => {
    e.preventDefault();
    try {
      await kanbanService.updateBoard(editingBoard.id, formData);
      toast.success('Tablero actualizado exitosamente');
      setShowEditModal(false);
      setEditingBoard(null);
      cargarTableros();
    } catch (error) {
      console.error('Error al actualizar tablero:', error);
      toast.error('Error al actualizar tablero');
    }
  };

  const handleDuplicateBoard = async (boardId, nombre) => {
    if (window.confirm(`¿Duplicar el tablero "${nombre}"?`)) {
      try {
        await kanbanService.duplicateBoard(boardId);
        toast.success('Tablero duplicado exitosamente');
        cargarTableros();
      } catch (error) {
        console.error('Error al duplicar tablero:', error);
        toast.error('Error al duplicar tablero');
      }
    }
  };

  const handleArchiveBoard = async (boardId, isArchived) => {
    const accion = isArchived ? 'desarchivar' : 'archivar';
    if (window.confirm(`¿Estás seguro de que quieres ${accion} este tablero?`)) {
      try {
        await kanbanService.archiveBoard(boardId);
        toast.success(`Tablero ${isArchived ? 'desarchivado' : 'archivado'} exitosamente`);
        cargarTableros();
      } catch (error) {
        console.error('Error al archivar tablero:', error);
        toast.error('Error al archivar tablero');
      }
    }
  };

  const handleDeleteBoard = async (boardId, nombre) => {
    if (window.confirm(`¿ELIMINAR el tablero "${nombre}"? Esta acción no se puede deshacer.\n\nTodas las tareas, estados y miembros serán eliminados permanentemente.`)) {
      try {
        await kanbanService.deleteBoard(boardId);
        toast.success('Tablero eliminado exitosamente');
        cargarTableros();
      } catch (error) {
        console.error('Error al eliminar tablero:', error);
        toast.error('Error al eliminar tablero');
      }
    }
  };

  const handleLeaveBoard = async (boardId, nombre) => {
    if (window.confirm(`¿Salir del tablero "${nombre}"?`)) {
      try {
        await kanbanService.leaveBoard(boardId);
        toast.success('Has salido del tablero exitosamente');
        cargarTableros();
      } catch (error) {
        console.error('Error al salir del tablero:', error);
        toast.error('Error al salir del tablero');
      }
    }
  };

  const openEditModal = (board) => {
    setEditingBoard(board);
    setFormData({
      nombre: board.nombre,
      descripcion: board.descripcion || '',
      icono: board.icono,
      color: board.color,
      is_private: board.is_private
    });
    setShowEditModal(true);
  };

  const getRolLabel = (rol) => {
    const roles = {
      'owner': 'Propietario',
      'editor': 'Editor',
      'viewer': 'Visualizador'
    };
    return roles[rol] || rol;
  };

  const filtrarTableros = (tableros, search) => {
    if (!search) return tableros;
    return tableros.filter(board =>
      board.nombre.toLowerCase().includes(search.toLowerCase()) ||
      (board.descripcion && board.descripcion.toLowerCase().includes(search.toLowerCase()))
    );
  };

  const tablerosFiltradosMis = filtrarTableros(
    mostrarArchivados ? misTableros.filter(b => b.is_archived) : misTableros.filter(b => !b.is_archived),
    searchMis
  );

  const tablerosFiltradosCompartidos = filtrarTableros(tablerosCompartidos, searchCompartidos);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="kanban-tableros-container">
      <div className="kanban-tableros-header">
        <div>
          <h1>📋 Tableros Kanban</h1>
          <p className="text-muted">Gestiona tus proyectos con tableros personalizables</p>
        </div>
        <div className="header-actions">
          <button
            className="btn-icon-round estado-btn"
            onClick={() => setMostrarArchivados(!mostrarArchivados)}
            title={mostrarArchivados ? "Ver activos" : "Ver archivados"}
          >
            <i className="fas fa-archive"></i>
          </button>
          <button
            className="btn-icon-round btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            <i className="fas fa-plus"></i>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="kanban-tabs">
        <button
          className={`tab-button ${activeTab === 'mis-tableros' ? 'active' : ''}`}
          onClick={() => setActiveTab('mis-tableros')}
        >
          Mis Tableros
          <span className="badge">{misTableros.filter(b => !b.is_archived).length}</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'compartidos' ? 'active' : ''}`}
          onClick={() => setActiveTab('compartidos')}
        >
          Compartidos Conmigo
          <span className="badge">{tablerosCompartidos.length}</span>
        </button>
      </div>

      {/* Contenido de tabs */}
      <div className="tab-content">
        {activeTab === 'mis-tableros' && (
          <div className="tab-panel">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Buscar mis tableros..."
                value={searchMis}
                onChange={(e) => setSearchMis(e.target.value)}
                className="form-control"
              />
            </div>

            <div className="tableros-grid">
              {tablerosFiltradosMis.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <h3>No hay tableros</h3>
                  <p>¡Crea tu primer tablero para comenzar!</p>
                  <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
                    <i className="fas fa-plus"></i> Crear Tablero
                  </button>
                </div>
              ) : (
                tablerosFiltradosMis.map(board => (
                  <TableroCard
                    key={board.id}
                    board={board}
                    esPropietario={true}
                    onEdit={openEditModal}
                    onDuplicate={handleDuplicateBoard}
                    onArchive={handleArchiveBoard}
                    onDelete={handleDeleteBoard}
                    onOpenBoard={() => setSelectedBoardId(board.id)}
                    getRolLabel={getRolLabel}
                  />
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'compartidos' && (
          <div className="tab-panel">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Buscar tableros compartidos..."
                value={searchCompartidos}
                onChange={(e) => setSearchCompartidos(e.target.value)}
                className="form-control"
              />
            </div>

            <div className="tableros-grid">
              {tablerosFiltradosCompartidos.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-users fa-3x"></i>
                  <h3>No hay tableros compartidos</h3>
                  <p>Cuando alguien te invite a un tablero, aparecerá aquí</p>
                </div>
              ) : (
                tablerosFiltradosCompartidos.map(board => (
                  <TableroCard
                    key={board.id}
                    board={board}
                    esPropietario={false}
                    onOpenBoard={() => setSelectedBoardId(board.id)}
                    onLeave={handleLeaveBoard}
                    getRolLabel={getRolLabel}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal del Tablero */}
      {selectedBoardId && (
        <KanbanTableroModal
          boardId={selectedBoardId}
          onClose={() => setSelectedBoardId(null)}
        />
      )}

      {/* Modal Crear Tablero */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content modal-l" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-content">
                <div className="modal-header-icon">📋</div>
                <div className="modal-header-text">
                  <h2 className="modal-header-title">Nuevo Tablero</h2>
                  <p className="modal-header-subtitle">Crea un nuevo tablero para organizar tus tareas</p>
                </div>
              </div>
              <button className="modal-close-btn" onClick={() => setShowCreateModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleCreateBoard}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nombre del tablero *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Descripción</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formData.descripcion}
                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Icono (emoji)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.icono}
                      onChange={(e) => setFormData({ ...formData, icono: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Color</label>
                    <input
                      type="color"
                      className="form-control"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_private}
                      onChange={(e) => setFormData({ ...formData, is_private: e.target.checked })}
                    />
                    <span>Tablero privado</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="estado-btn" onClick={() => setShowCreateModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  Crear Tablero
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar Tablero */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content modal-l" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-content">
                <div className="modal-header-icon">✏️</div>
                <div className="modal-header-text">
                  <h2 className="modal-header-title">Editar Tablero</h2>
                  <p className="modal-header-subtitle">Actualiza los detalles de tu tablero</p>
                </div>
              </div>
              <button className="modal-close-btn" onClick={() => setShowEditModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleEditBoard}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nombre del tablero *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Descripción</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formData.descripcion}
                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Icono (emoji)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.icono}
                      onChange={(e) => setFormData({ ...formData, icono: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Color</label>
                    <input
                      type="color"
                      className="form-control"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_private}
                      onChange={(e) => setFormData({ ...formData, is_private: e.target.checked })}
                    />
                    <span>Tablero privado</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="estado-btn" onClick={() => setShowEditModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente TableroCard
const TableroCard = ({ board, esPropietario, onEdit, onDuplicate, onArchive, onDelete, onLeave, onOpenBoard, getRolLabel }) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div
      className={`tablero-card ${board.is_archived ? 'archived' : ''}`}
      style={{ borderLeftColor: board.color }}
    >
      <div className="tablero-card-header">
        <h3>
          <span className="tablero-icon">{board.icono}</span>
          {board.nombre}
          {board.is_archived && <span className="badge badge-secondary">Archivado</span>}
        </h3>
        <div className="dropdown">
          <button
            className="btn-icon-small"
            onClick={() => setShowMenu(!showMenu)}
          >
            <i className="fas fa-ellipsis-v"></i>
          </button>
          {showMenu && (
            <div className="dropdown-menu show">
              {esPropietario ? (
                <>
                  <button onClick={() => { onEdit(board); setShowMenu(false); }}>
                    <i className="fas fa-edit"></i> Editar
                  </button>
                  <button onClick={() => { onDuplicate(board.id, board.nombre); setShowMenu(false); }}>
                    <i className="fas fa-copy"></i> Duplicar
                  </button>
                  <button onClick={() => { onArchive(board.id, board.is_archived); setShowMenu(false); }}>
                    <i className={`fas fa-${board.is_archived ? 'box-open' : 'archive'}`}></i>
                    {board.is_archived ? 'Desarchivar' : 'Archivar'}
                  </button>
                  <hr />
                  <button className="text-danger" onClick={() => { onDelete(board.id, board.nombre); setShowMenu(false); }}>
                    <i className="fas fa-trash"></i> Eliminar
                  </button>
                </>
              ) : (
                <button className="text-danger" onClick={() => { onLeave(board.id, board.nombre); setShowMenu(false); }}>
                  <i className="fas fa-sign-out-alt"></i> Salir del tablero
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <p className="tablero-descripcion">{board.descripcion || 'Sin descripción'}</p>

      <div className="tablero-stats">
        <span className="stat">
          <i className="fas fa-tasks"></i> {board.total_tareas || 0} tareas
        </span>
        <span className="stat">
          <i className="fas fa-users"></i> {board.total_miembros || 0} miembros
        </span>
        {board.is_private && (
          <span className="stat" title="Tablero privado">
            🔒
          </span>
        )}
      </div>

      {!esPropietario && (
        <div className="tablero-info">
          <small>
            <i className="fas fa-user"></i> Propietario: {board.propietario_nombre}
          </small>
          <small>
            <i className="fas fa-shield-alt"></i> Rol: {getRolLabel(board.rol_usuario)}
          </small>
        </div>
      )}

      <button onClick={onOpenBoard} className="btn-view-board">
        Ver Tablero
      </button>
    </div>
  );
};

export default KanbanTableros;